// ════════════════════════════════════════════════════════════════════
// Glass-i — Stage WO-1A · Work Orders API client.
// Base: /api/erp/production/work-orders. Mirrors the proformaApi /
// customerReceiptsApi axios + unwrap() pattern.
//   release  → turn a CONFIRMED proforma into a piece-tracked Work Order
//   list     → WO list grid (rollups: lines / pieces / completed)
//   get      → full detail (header + lines + pieces + barcodes)
// ════════════════════════════════════════════════════════════════════

import client from '../../../../api/client';

const BASE = '/api/erp/production/work-orders';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined ? resp.data.data : (resp ? resp.data : null);
}
const enc = encodeURIComponent;

export const workOrdersApi = {
  list: (params = {}) => client.get(BASE, { params }).then(unwrap),
  get: (id) => client.get(`${BASE}/${enc(id)}`).then(unwrap),
  release: (payload) => client.post(`${BASE}/release`, payload).then(unwrap),
  // F1 — set / change the expected delivery date (commitment) on a released WO.
  setDeliveryDate: (id, expectedDeliveryDate) =>
    client.patch(`${BASE}/${enc(id)}/delivery-date`, { expectedDeliveryDate }).then(unwrap),
  // WO-1B — advance a scanned piece one production stage.
  scan: (payload) => client.post(`${BASE}/scan`, payload).then(unwrap),
  // WO-1B.2 — scrap a piece → auto-issue a remake with a new barcode.
  scrap: (payload) => client.post(`${BASE}/scrap`, payload).then(unwrap),
  // WO-1B.2 — reprint ONE piece's QR sticker by barcode (remake / relabel).
  // Same raw-HTML contract as `stickers`.
  pieceSticker: (barcode) => client
    .get(`${BASE}/pieces/${enc(barcode)}/sticker`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
      transformResponse: [(d) => d]
    })
    .then((r) => r.data),
  // WO-3.3 — clear a piece's "reprint due" flag after its sticker was reprinted
  // with the re-tuned Change-Order spec. Returns { cleared }.
  clearPieceReprint: (barcode) => client
    .post(`${BASE}/pieces/${enc(barcode)}/sticker-reprinted`)
    .then((r) => r.data),
  // Piece QR stickers — a self-contained HTML document for the in-app iframe
  // viewer. `responseType:'text'` + identity transform so axios hands back the
  // raw HTML string (never JSON-parsed), matching the GRN receipt viewer.
  stickers: (id) => client
    .get(`${BASE}/${enc(id)}/stickers`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
      transformResponse: [(d) => d]
    })
    .then((r) => r.data),
  // Work Order "copy" sheet — A4 printable HTML for the in-app iframe viewer.
  // Same raw-HTML contract as `stickers`.
  workOrderCopy: (id) => client
    .get(`${BASE}/${enc(id)}/work-order-copy`, {
      responseType: 'text',
      headers: { Accept: 'text/html' },
      transformResponse: [(d) => d]
    })
    .then((r) => r.data),
  // Work Order copy as a real, paginated PDF (Blob) — exact, printer-safe page
  // breaks. Returned inline; the viewer embeds + prints it and offers download.
  // Mirrors dispatchPlanApi.loadingSlipPdf.
  workOrderCopyPdf: (id) => client
    .get(`${BASE}/${enc(id)}/work-order-copy.pdf`, {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' }
    })
    .then((r) => r.data)
};

export default workOrdersApi;
